import React, { useState } from 'react';
import { Trash2, Edit2, Check, X, Copy, Palette } from 'lucide-react';
import { WheelEntry } from '../../types';

interface EntryItemProps {
  entry: WheelEntry;
  index: number;
  segmentColor: string;
  onUpdate: (id: string, updates: Partial<WheelEntry>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (label: string, color?: string) => void;
  disabled?: boolean;
}

export const EntryItem: React.FC<EntryItemProps> = ({
  entry,
  index,
  segmentColor,
  onUpdate,
  onDelete,
  onDuplicate,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(entry.label);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleSave = () => {
    if (editLabel.trim() && editLabel.trim() !== entry.label) {
      onUpdate(entry.id, { label: editLabel.trim() });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditLabel(entry.label);
    setIsEditing(false);
  };

  const handleColorChange = (newColor: string) => {
    onUpdate(entry.id, { color: newColor });
    setShowColorPicker(false);
  };

  const handleResetColor = () => {
    onUpdate(entry.id, { color: undefined });
    setShowColorPicker(false);
  };

  const quickColors = ['#EF4444', '#F97316', '#F59E0B', '#10B981', '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899'];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        padding: '0.5rem 0.75rem',
        background: 'var(--bg-surface-elevated)',
        border: '1px solid var(--border-subtle)',
        borderRadius: '10px',
        position: 'relative',
        transition: 'all 0.15s ease',
      }}
    >
      {/* Segment Color Pill / Picker */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => !disabled && setShowColorPicker(!showColorPicker)}
          disabled={disabled}
          title="Change segment color"
          style={{
            width: '18px',
            height: '18px',
            borderRadius: '6px',
            background: entry.color || segmentColor,
            border: '2px solid rgba(255,255,255,0.4)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            display: 'block',
          }}
        />

        {showColorPicker && (
          <div
            className="glass-panel"
            style={{
              position: 'absolute',
              top: '24px',
              left: '0',
              zIndex: 30,
              padding: '8px',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '6px',
              width: '120px',
            }}
          >
            {quickColors.map((c) => (
              <button
                key={c}
                onClick={() => handleColorChange(c)}
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  background: c,
                }}
              />
            ))}
            <button
              onClick={handleResetColor}
              title="Reset to default theme"
              style={{
                gridColumn: 'span 4',
                fontSize: '0.7rem',
                color: 'var(--text-muted)',
                padding: '2px',
                textAlign: 'center',
              }}
            >
              Reset Color
            </button>
          </div>
        )}
      </div>

      {/* Index Number */}
      <span
        style={{
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--text-muted)',
          minWidth: '18px',
        }}
      >
        {index + 1}.
      </span>

      {/* Label or Edit Input */}
      {isEditing ? (
        <input
          type="text"
          value={editLabel}
          onChange={(e) => setEditLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          autoFocus
          style={{
            flex: 1,
            padding: '2px 6px',
            fontSize: '0.9rem',
          }}
        />
      ) : (
        <span
          style={{
            flex: 1,
            fontSize: '0.9rem',
            fontWeight: 500,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={entry.label}
        >
          {entry.label}
        </span>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {isEditing ? (
          <>
            <button
              onClick={handleSave}
              className="btn-ghost"
              style={{ padding: '4px', color: '#10b981' }}
              title="Save"
            >
              <Check size={15} />
            </button>
            <button
              onClick={handleCancel}
              className="btn-ghost"
              style={{ padding: '4px', color: '#ef4444' }}
              title="Cancel"
            >
              <X size={15} />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="btn-ghost"
              style={{ padding: '4px' }}
              title="Edit entry"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => onDuplicate(entry.label, entry.color)}
              disabled={disabled}
              className="btn-ghost"
              style={{ padding: '4px' }}
              title="Duplicate entry"
            >
              <Copy size={14} />
            </button>
            <button
              onClick={() => onDelete(entry.id)}
              disabled={disabled}
              className="btn-ghost"
              style={{ padding: '4px', color: '#ef4444' }}
              title="Delete entry"
            >
              <Trash2 size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
